import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT_LAYOUT, UI_CENTER_X, UI_CENTER_Y } from '../core/Config';
import { GameSettings } from '../core/GameSettings';
import { pulseVirtualAction } from '../core/VirtualControls';

export class SettingsPanel {
  private readonly container: Phaser.GameObjects.Container;
  private readonly labels: Phaser.GameObjects.Text[] = [];
  private readonly values: Phaser.GameObjects.Text[] = [];
  private readonly rows: Phaser.GameObjects.Rectangle[] = [];
  private selectedIndex = 0;
  private visibleState = false;

  constructor(private readonly scene: Phaser.Scene) {
    const mobile = IS_PORTRAIT_LAYOUT;
    const panelWidth = mobile ? GAME_WIDTH - 56 : 660;
    const panelHeight = mobile ? 1360 : 700;
    const rowStartY = mobile ? -430 : -245;
    const rowGap = mobile ? 98 : 54;

    const dim = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x050806, 0.62).setInteractive();
    const shadow = scene.add.rectangle(8, 10, panelWidth, panelHeight, 0x000000, 0.34);
    const outer = scene.add
      .rectangle(0, 0, panelWidth, panelHeight, 0x172019, 0.99)
      .setStrokeStyle(mobile ? 5 : 3, 0xe8c768, 0.96);
    const inner = scene.add
      .rectangle(0, 0, panelWidth - 28, panelHeight - 28, 0x0c120f, 0.99)
      .setStrokeStyle(2, 0x8ebc98, 0.72);
    const title = scene.add.text(0, mobile ? -590 : -314, '게임 설정', {
      fontSize: mobile ? '42px' : '30px',
      color: '#fff0b5',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    const subtitle = scene.add.text(0, mobile ? -535 : -275, mobile ? '항목을 누르면 바로 변경됩니다' : '방향키 선택 · Enter 변경', {
      fontSize: mobile ? '27px' : '17px',
      color: '#b9ddbf',
    }).setOrigin(0.5);
    const closeBg = scene.add
      .rectangle(panelWidth / 2 - (mobile ? 74 : 58), -panelHeight / 2 + (mobile ? 62 : 42), mobile ? 116 : 88, mobile ? 66 : 42, 0x3a4438, 0.98)
      .setStrokeStyle(2, 0xa9c3a4, 0.75)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => pulseVirtualAction('cancel'));
    const closeText = scene.add.text(closeBg.x, closeBg.y, '닫기', {
      fontSize: mobile ? '28px' : '18px',
      color: '#f2f4ed',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    const children: Phaser.GameObjects.GameObject[] = [dim, shadow, outer, inner, title, subtitle, closeBg, closeText];
    for (let index = 0; index < 10; index += 1) {
      const y = rowStartY + index * rowGap;
      const row = scene.add
        .rectangle(0, y, panelWidth - (mobile ? 78 : 72), mobile ? 78 : 44, 0x233129, 0.88)
        .setStrokeStyle(2, 0x6f8d75, 0.55)
        .setInteractive({ useHandCursor: true })
        .on('pointerdown', () => {
          this.selectedIndex = index;
          this.render();
          pulseVirtualAction('confirm');
        });
      const label = scene.add.text(-panelWidth / 2 + (mobile ? 76 : 58), y - (mobile ? 22 : 13), '', {
        fontSize: mobile ? '28px' : '20px',
        color: '#eef4ec',
      });
      const value = scene.add.text(panelWidth / 2 - (mobile ? 76 : 58), y - (mobile ? 22 : 13), '', {
        fontSize: mobile ? '28px' : '20px',
        color: '#b9ddbf',
        fontStyle: 'bold',
      }).setOrigin(1, 0);
      children.push(row, label, value);
      this.rows.push(row);
      this.labels.push(label);
      this.values.push(value);
    }

    const footer = scene.add.text(0, mobile ? 590 : 310, mobile ? 'B 버튼으로 닫기' : 'O / Esc 닫기', {
      fontSize: mobile ? '27px' : '16px',
      color: '#a9c3a4',
    }).setOrigin(0.5);
    children.push(footer);

    this.container = scene.add
      .container(UI_CENTER_X, UI_CENTER_Y, children)
      .setDepth(12100)
      .setScrollFactor(0)
      .setVisible(false);
    this.render();
  }

  toggle() {
    this.visibleState = !this.visibleState;
    this.container.setVisible(this.visibleState);
    this.render();
  }

  hide() {
    this.visibleState = false;
    this.container.setVisible(false);
  }

  isVisible() {
    return this.visibleState;
  }

  move(delta: number) {
    this.selectedIndex = Phaser.Math.Wrap(this.selectedIndex + delta, 0, this.labels.length);
    this.render();
  }

  confirm() {
    const settings = GameSettings.getAll();
    if (this.selectedIndex === 0) GameSettings.set('alwaysRun', !settings.alwaysRun);
    if (this.selectedIndex === 1) GameSettings.set('showSourceBadge', !settings.showSourceBadge);
    if (this.selectedIndex === 2) GameSettings.set('cameraZoom', settings.cameraZoom >= 1.2 ? 1 : 1.2);
    if (this.selectedIndex === 3) GameSettings.set('textSpeed', settings.textSpeed >= 1.5 ? 0.75 : Number((settings.textSpeed + 0.25).toFixed(2)));
    if (this.selectedIndex === 4) GameSettings.set('uiMotion', !settings.uiMotion);
    if (this.selectedIndex === 5) GameSettings.set('interactionBubble', !settings.interactionBubble);
    if (this.selectedIndex === 6) {
      const nextScale = settings.mobileControlScale >= 1.15
        ? 0.85
        : Number((settings.mobileControlScale + 0.15).toFixed(2));
      GameSettings.set('mobileControlScale', nextScale);
    }
    if (this.selectedIndex === 7) {
      const nextOpacity = settings.mobileControlOpacity >= 1
        ? 0.55
        : settings.mobileControlOpacity < 0.7 ? 0.75 : 1;
      GameSettings.set('mobileControlOpacity', nextOpacity);
    }
    if (this.selectedIndex === 8) {
      GameSettings.set('mobileControlLayout', settings.mobileControlLayout === 'standard' ? 'reversed' : 'standard');
    }
    if (this.selectedIndex === 9) GameSettings.set('hapticFeedback', !settings.hapticFeedback);
    this.render();
  }

  private render() {
    const settings = GameSettings.getAll();
    const labels = [
      '항상 달리기',
      '데이터 배지 표시',
      '카메라 확대',
      '대화 출력 속도',
      'UI 움직임',
      '상호작용 말풍선',
      '모바일 조작 크기',
      '모바일 조작 투명도',
      '모바일 좌우 배치',
      '모바일 터치 진동',
    ];
    const values = [
      settings.alwaysRun ? '켜짐' : '꺼짐',
      settings.showSourceBadge ? '켜짐' : '꺼짐',
      `x${settings.cameraZoom.toFixed(1)}`,
      `x${settings.textSpeed.toFixed(2)}`,
      settings.uiMotion ? '켜짐' : '꺼짐',
      settings.interactionBubble ? '켜짐' : '꺼짐',
      settings.mobileControlScale < 1 ? '작게' : settings.mobileControlScale > 1 ? '크게' : '보통',
      `${Math.round(settings.mobileControlOpacity * 100)}%`,
      settings.mobileControlLayout === 'reversed' ? '반대' : '기본',
      settings.hapticFeedback ? '켜짐' : '꺼짐',
    ];

    this.labels.forEach((label, index) => {
      const selected = index === this.selectedIndex;
      label.setText(`${selected ? '> ' : '  '}${labels[index]}`).setColor(selected ? '#ffe391' : '#eef4ec');
      this.values[index].setText(values[index]).setColor(selected ? '#ffe391' : '#b9ddbf');
      this.rows[index]
        .setFillStyle(selected ? 0x4d684f : 0x233129, selected ? 0.96 : 0.86)
        .setStrokeStyle(2, selected ? 0xe8c768 : 0x6f8d75, selected ? 0.92 : 0.55);
    });
  }
}
